import { useEffect, useMemo, useState } from 'react'
import { useLocation, Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  LayoutDashboard,
  Briefcase,
  Users,
  UserRound,
  ClipboardList,
  Calendar,
  GanttChart,
  Receipt,
  FileText,
  Settings,
  Activity,
  ChevronDown,
  Building2,
  LogOut,
  User,
  ShieldCheck,
  MapPin,
  GitBranch,
  Check,
  // Route,
  ListChecks,
  CalendarDays,
  Landmark,
  KeyRound,
  Package2,
  ShoppingCart,
  TicketPercent,
  Layers3,
  MessageCircle,
  FolderTree,
  BarChart3,
  Heart,
  Megaphone,
  Package,
  Wrench,
  Search,
  CircleDollarSign,
} from 'lucide-react'

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/stores/auth-store'
import { authApi } from '@/services/auth'
import { orgTreeApi, orgServiceInstanceLightQueryKey, type OrgTreeServiceInstance } from '@/services/org-tree'
import { orgUsersApi } from '@/services/org-users'
import { policyFeaturesApi, type PolicyFeature } from '@/services/policy-features'
import { venueApi, type VenueSidebarConfigRecord } from '@/services/venue'
import {
  buildEffectiveCapabilities,
} from '@/lib/effective-capabilities'
import { orgLevelVariantsApi } from '@/services/org-level-variants'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import { evaluateRouteAccess } from '@/lib/route-access'
import { getInitials } from '@/lib/utils'
import {
  buildServiceVariantHomePath,
  buildServiceVariantHomePathFromVariant,
  buildOrgServiceInstanceFormPath,
  getServiceInstanceIdFromSearch,
  getServiceInstanceScopeLabelFromInstance,
  getNodeLinkedServiceInstanceIds,
  resolveActiveServiceInstanceId,
  resolveFundraisingScopedServiceInstanceId,
  resolveWorkspaceActiveServiceInstanceId,
  resolveWorkspacePostContext,
  withServiceInstanceId,
} from '@/lib/service-instance'
import { resolveNodeServicePermissionOption, getServiceInstanceVariantDisplayName, buildNodeServicePermissionOptionsFromPosts, mergeNodeServicePermissionOptions } from '@/lib/node-role-services'
import { useNodeLinkedServiceInstances, type NodeServicePermissionOption } from '@/hooks/use-node-linked-service-instances'
import { isBranchOperatorWorkspaceContext, isBranchOperatorWorkspaceContextFromPost } from '@/lib/org-hierarchy-from-instance'
import {
  isOrganizationServiceVariant,
  isInAppChildServiceVariant,
  isMembershipServiceVariant,
  isRootOrganizationServiceVariant,
  isServicesProductVariant,
  isTenantOrganizationServiceVariant,
  readServiceVariantIdentity,
} from '@/lib/service-variant-types'
import { getDefaultChildLevelVariantUniqueIds } from '@/lib/level-variant-defaults'
import { resolveServiceInstanceAuthLogos } from '@/lib/auth-branding'

const APPROVED_STATUS_LABEL = 'Approved'
const SHOW_IN_APPS_FEATURE = true

const membershipSidebarEnvValue = String(
  (import.meta as unknown as { env?: Record<string, unknown> }).env?.MEMBERSHIP
  ?? (import.meta as unknown as { env?: Record<string, unknown> }).env?.VITE_MEMBERSHIP
  ?? '',
).trim()

const projectMgmtSidebarEnvValue = String(
  (import.meta as unknown as { env?: Record<string, unknown> }).env?.PROJECT_MGMT
  ?? (import.meta as unknown as { env?: Record<string, unknown> }).env?.VITE_PROJECT_MGMT
  ?? '',
).trim()

const eventMgmtSidebarEnvValue = String(
  (import.meta as unknown as { env?: Record<string, unknown> }).env?.EVENT_MGMT
  ?? (import.meta as unknown as { env?: Record<string, unknown> }).env?.VITE_EVENT_MGMT
  ?? '',
).trim()

const SHOW_MEMBERSHIP_SIDEBAR = membershipSidebarEnvValue.length > 0
const SHOW_PROJECT_MGMT_SIDEBAR = projectMgmtSidebarEnvValue.length > 0
const SHOW_EVENT_MGMT_SIDEBAR = eventMgmtSidebarEnvValue
  ? !['0', 'false', 'no', 'off'].includes(eventMgmtSidebarEnvValue.toLowerCase())
  : true
const FUNDRAISING_VARIANT_IDS = new Set(['1600'])
const PROJECT_VARIANT_IDS = new Set(['200'])
const EVENT_VARIANT_IDS = new Set(['300'])
const VENUE_VARIANT_IDS = new Set(['2000', '2100', '2200', '2300'])

const mapCustomerFacingServicesTerm = (value: string) => {
  const normalized = String(value || '').trim()
  if (!normalized) {
    return normalized
  }

  if (normalized.toLowerCase() === 'services') {
    return 'Apps'
  }

  return normalized
}

const isFundraisingContextValue = (value: unknown) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) {
    return false
  }

  if (FUNDRAISING_VARIANT_IDS.has(normalized)) {
    return true
  }

  return normalized.includes('fundraising')
}

const isEventContextValue = (value: unknown) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) {
    return false
  }

  if (EVENT_VARIANT_IDS.has(normalized)) {
    return true
  }

  return normalized === 'event' || normalized === 'events' || normalized.includes('event')
}

const isVenueContextValue = (value: unknown) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) {
    return false
  }

  if (VENUE_VARIANT_IDS.has(normalized)) {
    return true
  }

  return (
    normalized.includes('temple')
    || normalized.includes('church')
    || normalized.includes('charity')
    || normalized.includes('mosque')
    || normalized.includes('synagogue')
    || normalized.includes('venue')
  )
}

const isProjectContextValue = (value: unknown) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) {
    return false
  }

  if (PROJECT_VARIANT_IDS.has(normalized)) {
    return true
  }

  return normalized === 'project' || normalized === 'projects' || normalized === 'ngo' || normalized.includes('project')
}

const getServiceRegistrationStatusLabel = (status?: string | number | null): string | null => {
  if (status === null || status === undefined || status === '') {
    return null
  }

  const normalized = String(status).trim().toLowerCase()

  if (normalized === '1' || normalized === 'approved' || normalized === 'active') {
    return APPROVED_STATUS_LABEL
  }

  if (normalized === '0' || normalized === 'pending') {
    return 'Pending'
  }

  if (normalized === '2' || normalized === 'rejected') {
    return 'Rejected'
  }

  if (normalized === '3' || normalized === 'suspended') {
    return 'Suspended'
  }

  if (normalized === '4' || normalized === 'terminated') {
    return 'Terminated'
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

const resolveSidebarOrganizationLogo = (instance: OrgTreeServiceInstance | null | undefined) =>
  resolveServiceInstanceAuthLogos(instance).sidePanelLogo

const getStatusBadgeClassName = (statusLabel: string): string => {
  const normalized = statusLabel.trim().toLowerCase()

  if (normalized === 'pending' || normalized === 'suspended') {
    return 'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-900/70 dark:bg-amber-900/40 dark:text-amber-100'
  }

  if (normalized === 'rejected' || normalized === 'terminated') {
    return 'border-rose-200 bg-rose-100 text-rose-900 dark:border-rose-900/70 dark:bg-rose-900/40 dark:text-rose-100'
  }

  return 'border-border/70 bg-muted/40 text-muted-foreground'
}

const isPendingRegistrationStatus = (status: unknown) => {
  const normalized = String(status ?? '').trim().toLowerCase()
  return normalized === '1' || normalized === 'pending'
}

const resolveLevelVariantDirectoryPath = (
  levelVariant: {
    unique_id?: string | null
    vertical_unique_id?: string | null
    vertical_system_code?: string | null
    vertical_name?: string | null
  } | null | undefined,
) => {
  const uniqueId = String(levelVariant?.unique_id ?? '').trim()
  if (uniqueId === 'head-tenant-lvl-variant' || uniqueId === 'tenant-lvl-variant') {
    return '/org/tenants'
  }
  if (uniqueId === 'branch-lvl-variant') {
    return '/org/service-instance'
  }

  const verticalUniqueId = String(
    levelVariant?.vertical_unique_id
    ?? levelVariant?.vertical_system_code
    ?? levelVariant?.vertical_name
    ?? '',
  ).trim().toUpperCase()

  return verticalUniqueId === 'TENANT' ? '/org/tenants' : '/org/service-instance'
}

const SYSTEM_VERTICAL_CODE_TO_LEVEL_VARIANT_UNIQUE_ID: Record<string, string> = {
  ROOT_ORG: 'root-org-lvl-variant',
  HEAD_TENANT: 'head-tenant-lvl-variant',
  TENANT: 'tenant-lvl-variant',
  BRANCH: 'branch-lvl-variant',
}

const findLevelVariantByUniqueId = <T extends { unique_id?: string | null }>(
  rows: T[],
  uniqueId: string,
) => rows.find((row) => String(row.unique_id ?? '').trim() === uniqueId) ?? null

const readLevelVariantDisplayName = (row: {
  id?: string | number | null
  unique_id?: string | null
  variant_name?: string | null
  variant_name_text?: string | null
  name?: string | null
} | null | undefined) => {
  if (!row) {
    return ''
  }
  return String(row.variant_name_text || row.variant_name || row.name || `Level ${row.id}`).trim() || `Level ${row.id}`
}

const readPostLevelVariantUniqueId = (
  post: ReturnType<typeof useAuthStore.getState>['currentPost'],
  activeServiceInstance?: {
    level_variant_unique_id?: string | null
    tier_code?: string | null
    level_variant_vertical_system_code?: string | null
  } | null,
) => {
  const rolesData = post?.roles_data as Record<string, unknown> | null | undefined
  const directUniqueId = String(
    rolesData?.level_variant_unique_id
    ?? rolesData?.tier_code
    ?? post?.level_variant_unique_id
    ?? activeServiceInstance?.level_variant_unique_id
    ?? activeServiceInstance?.tier_code
    ?? '',
  ).trim()
  if (directUniqueId) {
    return directUniqueId
  }

  const systemCode = String(activeServiceInstance?.level_variant_vertical_system_code ?? '').trim().toUpperCase()
  return SYSTEM_VERTICAL_CODE_TO_LEVEL_VARIANT_UNIQUE_ID[systemCode] ?? ''
}

const marketingNavigationItem = {
  title: 'Announcements',
  url: '/marketing/announcements',
  icon: Megaphone,
} as const

const projectNavigation: SidebarNavigationEntry[] = [
  {
    title: 'Projects',
    url: '/projects',
    icon: FolderTree,
  },
  {
    title: 'Customers',
    url: '/projects/customers',
    icon: Users,
  },
  {
    title: 'Assignments',
    url: '/projects/assignments',
    icon: ClipboardList,
  },
  {
    title: 'Locations',
    url: '/project/locations',
    icon: MapPin,
  },
  {
    title: 'Work Days',
    url: '/projects/workdays',
    icon: CalendarDays,
  },
  {
    title: 'Checklists',
    url: '/projects/checklists',
    icon: ListChecks,
  },
  {
    title: 'Configuration',
    url: '/projects/configuration',
    icon: Settings,
  },
  {
    title: 'Workflows',
    url: '/projects/workflows',
    icon: GitBranch,
  },
  marketingNavigationItem,
]

const eventNavigation: SidebarLeafNavigationEntry[] = [
  {
    title: 'Events',
    url: '/events',
    icon: CalendarDays,
  },
  {
    title: 'Customers',
    url: '/events/customers',
    icon: Users,
  },
  {
    title: 'Assignments',
    url: '/events/assignments',
    icon: ClipboardList,
  },
  {
    title: 'Locations',
    url: '/event/locations',
    icon: MapPin,
  },
  {
    title: 'Event Days',
    url: '/events/workdays',
    icon: Calendar,
  },
  {
    title: 'Configuration',
    url: '/events/configuration',
    icon: Settings,
  },
  {
    title: 'Workflows',
    url: '/events/workflows',
    icon: GitBranch,
  },
  marketingNavigationItem,
]

const navigation: SidebarNavigationEntry[] = [
  {
    title: 'Jobs',
    url: '/job',
    icon: Briefcase,
  },
  {
    title: 'Customers',
    url: '/job/customers',
    icon: Users,
  },
  {
    title: 'Workers',
    url: '/job/workers',
    icon: Users,
  },
  {
    title: 'Vehicles',
    url: '/vehicles',
    icon: ClipboardList,
  },
  {
    title: 'Locations',
    url: '/locations',
    icon: MapPin,
  },
  {
    title: 'Live',
    url: '/live',
    icon: Activity,
  },
  {
    title: 'Assignments',
    url: '/assignments',
    icon: ClipboardList,
  },
  {
    title: 'Worker Skills',
    url: '/services/worker-skills',
    icon: ShieldCheck,
  },
  {
    title: 'Teams',
    url: '/services/crews',
    icon: Users,
  },
  {
    title: 'Work Days',
    url: '/workdays',
    icon: Calendar,
  },
  {
    title: 'Timeline',
    url: '/timeline',
    icon: GanttChart,
  },
  {
    title: 'Configuration',
    url: '/job/configuration',
    icon: Settings,
  },
  {
    title: 'Workflows',
    url: '/job/workflows',
    icon: GitBranch,
  },
  {
    title: 'Complaints',
    url: '/job/complaints',
    icon: MessageCircle,
  },
  marketingNavigationItem,
]

const organisationNavigation = [
  {
    title: 'Branches',
    url: '/org/service-instance',
    icon: GitBranch,
  },
]


const organisationSupportNavigation = [
  {
    title: 'Roles',
    url: '/org/roles',
    icon: ShieldCheck,
  },
  {
    title: 'Features',
    url: '/org/features',
    icon: KeyRound,
  },
]

const assetManagementNavigation: SidebarLeafNavigationEntry[] = [
  {
    title: 'Assets',
    url: '/assets',
    icon: Package,
  },
  {
    title: 'Maintenance',
    url: '/assets/maintenance',
    icon: Wrench,
  },
  {
    title: 'Contracts',
    url: '/assets/contracts',
    icon: FileText,
  },
  {
    title: 'Lookup',
    url: '/assets/lookup',
    icon: Search,
  },
  {
    title: 'Locations',
    url: '/assets/locations',
    icon: MapPin,
  },
  {
    title: 'My assets',
    url: '/assets/my',
    icon: UserRound,
  },
  {
    title: 'Reports',
    url: '/assets/reports',
    icon: BarChart3,
  },
  {
    title: 'Settings',
    url: '/assets/configuration',
    icon: Settings,
  },
]

const financeNavigation: SidebarNavigationEntry[] = [
  {
    title: 'Reports',
    icon: FileText,
    items: [
      { title: 'Monthwise', url: '/reports/monthwise' },
      { title: 'Company', url: '/reports/company' },
      { title: 'Worker', url: '/reports/worker' },
      { title: 'Client', url: '/reports/client' },
    ],
  },
  {
    title: 'Expenses',
    url: '/job/expenses',
    icon: Receipt,
  },
  {
    title: 'Invoices',
    icon: FileText,
    items: [
      { title: 'Customer', url: '/finance/invoices' },
      { title: 'Worker', url: '/job/worker/invoices' },
      { title: 'Settlements', url: '/finance/settlement-invoices' },
    ],
  },
  {
    title: 'Quotations',
    url: '/services/quotations',
    icon: FileText,
  },
  {
    title: 'Inventory',
    url: '/job/particulars',
    icon: ClipboardList,
  },
  {
    title: 'Taxes',
    url: '/finance/taxes',
    icon: Receipt,
  },
]

const STORE_SIDEBAR_CAPABILITY = {
  branchServiceProducts: 'can_access_branch_service_products',
  orders: 'can_access_service_orders',
  offers: 'can_access_service_offers',
  resourcePlanner: 'can_access_resource_planner',
} as const

const storeNavigation = [
  {
    title: 'Services',
    url: '/services/products',
    icon: Layers3,
    capabilityKeys: [STORE_SIDEBAR_CAPABILITY.branchServiceProducts],
  },
  {
    title: 'Customers',
    url: '/services/customers',
    icon: Users,
    capabilityKeys: [STORE_SIDEBAR_CAPABILITY.branchServiceProducts, STORE_SIDEBAR_CAPABILITY.orders],
  },
  {
    title: 'Packages',
    url: '/services/product-variants',
    icon: Package2,
    capabilityKeys: [STORE_SIDEBAR_CAPABILITY.branchServiceProducts],
  },
  {
    title: 'Offers',
    url: '/services/offers',
    icon: TicketPercent,
    capabilityKeys: [STORE_SIDEBAR_CAPABILITY.offers],
  },
  {
    title: 'Resource Planner',
    url: '/planner',
    icon: Calendar,
    capabilityKeys: [STORE_SIDEBAR_CAPABILITY.resourcePlanner],
  },
  {
    title: 'Bookings',
    url: '/services/orders',
    icon: ShoppingCart,
    capabilityKeys: [STORE_SIDEBAR_CAPABILITY.orders],
  },
  {
    title: 'Payments',
    icon: Receipt,
    items: [
      { title: 'Payments', url: '/services/order-payments' },
      { title: 'Refunds', url: '/services/refunds' },
    ],
  },
  marketingNavigationItem,
] as const

const membershipNavigation = [
  {
    title: 'Dashboard',
    url: '/membership/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'Categories',
    url: '/services/memberships?tab=categories',
    icon: FolderTree,
  },
  {
    title: 'Plans',
    url: '/services/memberships?tab=plans',
    icon: Layers3,
  },
  {
    title: 'Packages',
    url: '/services/memberships?tab=packages',
    icon: Package2,
  },
  {
    title: 'Subscriptions',
    url: '/services/memberships?tab=subscriptions',
    icon: Users,
  },
  {
    title: 'Refunds',
    url: '/services/memberships?tab=refunds',
    icon: FileText,
  },
  {
    title: 'Renewals',
    url: '/services/memberships?tab=renewals',
    icon: Calendar,
  },
  {
    title: 'Reports',
    url: '/services/memberships?tab=reports',
    icon: BarChart3,
  },
  {
    title: 'Configuration',
    url: '/services/memberships?tab=configuration',
    icon: Settings,
  },
  {
    title: 'Workflow',
    url: '/services/memberships?tab=workflow',
    icon: ListChecks,
  },
  marketingNavigationItem,
] as const

const venueNavigation: SidebarNavigationEntry[] = [
  {
    title: 'Dashboard',
    url: '/venue/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'Trustees',
    url: '/venue/figures',
    icon: Users,
  },
  {
    title: 'Deities',
    url: '/venue/deities',
    icon: Landmark,
  },
  {
    title: 'Festivals',
    url: '/events',
    icon: CalendarDays,
  },
  {
    title: 'Volunteers',
    url: '/venue/volunteers',
    icon: UserRound,
  },
  {
    title: 'Events',
    url: '/events',
    icon: Calendar,
  },
  {
    title: 'News',
    url: '/venue/news',
    icon: Megaphone,
  },
]

const venueFundraisingNavigationItem: SidebarLeafNavigationEntry = {
  title: 'Fundraising',
  url: '/fundraising',
  icon: Heart,
}

const venueFundraisingCampaignsNavigationItem: SidebarLeafNavigationEntry = {
  title: 'Campaigns',
  url: '/fundraising',
  icon: Heart,
}

const venueFundraisingDonationsNavigationItem: SidebarLeafNavigationEntry = {
  title: 'Donations',
  url: '/fundraising/donations',
  icon: CircleDollarSign,
}

const venueFundraisingCategoriesNavigationItem: SidebarLeafNavigationEntry = {
  title: 'Category',
  url: '/fundraising/categories',
  icon: Layers3,
}

const venueFundraisingDonorsNavigationItem: SidebarLeafNavigationEntry = {
  title: 'Donors',
  url: '/fundraising/donors',
  icon: Users,
}

const venueFundraisingSettingsNavigationItem: SidebarLeafNavigationEntry = {
  title: 'Settings',
  url: '/fundraising/settings',
  icon: Settings,
}

const venueAdminHomeContentNavigationItem: SidebarLeafNavigationEntry = {
  title: 'Home Content',
  url: '/venue/admin/home',
  icon: FileText,
}

const venueAdminPoojaTimingsNavigationItem: SidebarLeafNavigationEntry = {
  title: 'Pooja Timings',
  url: '/venue/admin/pooja-timings',
  icon: Calendar,
}

const venueConfigRouteTemplates: SidebarLeafNavigationEntry[] = [
  ...venueNavigation.filter((item): item is SidebarLeafNavigationEntry => (
    typeof (item as { url?: string }).url === 'string'
    && String((item as { url?: string }).url).length > 0
  )),
  venueFundraisingNavigationItem,
  venueFundraisingCampaignsNavigationItem,
  venueFundraisingDonationsNavigationItem,
  venueFundraisingCategoriesNavigationItem,
  venueFundraisingDonorsNavigationItem,
  venueFundraisingSettingsNavigationItem,
  venueAdminHomeContentNavigationItem,
  venueAdminPoojaTimingsNavigationItem,
]

const venueSidebarKeyToPath = new Map<string, string>([
  ['dashboard', '/venue/dashboard'],
  ['home', '/venue/dashboard'],
  ['admin', '/venue/dashboard'],
  ['admin dashboard', '/venue/dashboard'],
  ['trustees', '/venue/figures'],
  ['figures', '/venue/figures'],
  ['deities', '/venue/deities'],
  ['saints', '/venue/figures'],
  ['festival', '/events'],
  ['festivals', '/events'],
  ['volunteer', '/venue/volunteers'],
  ['volunteers', '/venue/volunteers'],
  ['events', '/events'],
  ['calendar', '/events'],
  ['news', '/venue/news'],
  ['articles', '/venue/news'],
  ['home content', '/venue/admin/home'],
  ['homepage content', '/venue/admin/home'],
  ['home page content', '/venue/admin/home'],
  ['pooja timings', '/venue/admin/pooja-timings'],
  ['ritual timings', '/venue/admin/pooja-timings'],
  ['fundraising', '/fundraising'],
  ['fundraising dashboard', '/fundraising/dashboard'],
  ['campaigns', '/fundraising'],
  ['fundraising campaigns', '/fundraising'],
  ['donations', '/fundraising/donations'],
  ['fundraising donations', '/fundraising/donations'],
  ['categories', '/fundraising/categories'],
  ['category', '/fundraising/categories'],
  ['fundraising categories', '/fundraising/categories'],
  ['donors', '/fundraising/donors'],
  ['fundraising donors', '/fundraising/donors'],
  ['fundraising settings', '/fundraising/settings'],
])

const normalizeVenueSidebarPath = (value: unknown): string => {
  const path = String(value ?? '').trim()
  if (!path) {
    return ''
  }

  if (path === '/venue' || path === 'venue') {
    return '/venue/dashboard'
  }

  const normalized = path.startsWith('/') ? path : `/${path}`
  if (normalized === '/venue/fundraising') {
    return '/fundraising'
  }
  if (normalized === '/venue/events' || normalized === '/venue/calendar') {
    return '/events'
  }

  return normalized
}

const normalizeVenueSidebarConfigNavigation = (
  sidebarConfig: VenueSidebarConfigRecord | null | undefined,
): SidebarLeafNavigationEntry[] => {
  const defaults = [...venueNavigation.filter(hasNavigationUrl)]
  const pathToDefault = new Map(venueConfigRouteTemplates.map((item) => [item.url, item]))

  const items = Array.isArray(sidebarConfig?.menu_items) ? sidebarConfig?.menu_items : []
  const normalizedEntries = items.reduce<SidebarLeafNavigationEntry[]>((result, item) => {
    const itemRecord = (item && typeof item === 'object') ? item as Record<string, unknown> : null
    const labelCandidate = String(
      itemRecord?.label
      ?? itemRecord?.title
      ?? itemRecord?.name
      ?? item
      ?? '',
    ).trim()

    const keyPath = venueSidebarKeyToPath.get(labelCandidate.toLowerCase()) ?? ''
    const configuredPath = normalizeVenueSidebarPath(itemRecord?.path ?? itemRecord?.url)
    const resolvedPath = configuredPath || keyPath
    if (!resolvedPath) {
      return result
    }

    const template = pathToDefault.get(resolvedPath)
    if (!template) {
      return result
    }

    const title = labelCandidate || template.title
    if (result.some((entry) => entry.url === template.url)) {
      return result
    }

    result.push({
      ...template,
      title,
    })
    return result
  }, [])

  const fallbackEntries = normalizedEntries.length > 0 ? normalizedEntries : defaults
  const eventsEnabled = sidebarConfig?.is_events_enabled !== false
  const fundraisingEnabled = sidebarConfig?.is_fundraising_enabled !== false

  return fallbackEntries.filter((entry) => {
    if ((entry.url === '/venue/events' || entry.url === '/events') && !eventsEnabled) {
      return false
    }
    if (entry.url.startsWith('/fundraising') && !fundraisingEnabled) {
      return false
    }
    return true
  })
}

const normalizeVenueSidebarConfigAdminNavigation = (
  sidebarConfig: VenueSidebarConfigRecord | null | undefined,
): SidebarLeafNavigationEntry[] => {
  const pathToDefault = new Map(venueConfigRouteTemplates.map((item) => [item.url, item]))
  const items = Array.isArray(sidebarConfig?.admin_menu_items) ? sidebarConfig.admin_menu_items : []

  const eventsEnabled = sidebarConfig?.is_events_enabled !== false
  const fundraisingEnabled = sidebarConfig?.is_fundraising_enabled !== false

  const normalized = items.reduce<SidebarLeafNavigationEntry[]>((result, item) => {
    const itemRecord = (item && typeof item === 'object') ? item as Record<string, unknown> : null
    const labelCandidate = String(
      itemRecord?.label
      ?? itemRecord?.title
      ?? itemRecord?.name
      ?? item
      ?? '',
    ).trim()

    const keyPath = venueSidebarKeyToPath.get(labelCandidate.toLowerCase()) ?? ''
    const configuredPath = normalizeVenueSidebarPath(itemRecord?.path ?? itemRecord?.url)
    const resolvedPath = configuredPath || keyPath
    if (!resolvedPath) {
      return result
    }

    const template = pathToDefault.get(resolvedPath)
    if (!template) {
      return result
    }

    if ((template.url === '/venue/events' || template.url === '/events') && !eventsEnabled) {
      return result
    }

    if (template.url.startsWith('/fundraising') && !fundraisingEnabled) {
      return result
    }

    if (result.some((entry) => entry.url === template.url)) {
      return result
    }

    result.push({
      ...template,
      title: labelCandidate || template.title,
    })
    return result
  }, [])

  const ensured = [...normalized]
  if (!ensured.some((entry) => entry.url === venueAdminHomeContentNavigationItem.url)) {
    ensured.push(venueAdminHomeContentNavigationItem)
  }
  if (!ensured.some((entry) => entry.url === venueAdminPoojaTimingsNavigationItem.url)) {
    ensured.push(venueAdminPoojaTimingsNavigationItem)
  }

  if (ensured.length > 0) {
    return ensured
  }

  return [venueAdminHomeContentNavigationItem, venueAdminPoojaTimingsNavigationItem]
}

const fundraisingNavigation: SidebarNavigationEntry[] = [
  {
    title: 'Campaigns',
    url: '/fundraising',
    icon: Heart,
  },
  {
    title: 'Donations',
    url: '/fundraising/donations',
    icon: CircleDollarSign,
  },
  {
    title: 'Donors',
    url: '/fundraising/donors',
    icon: Users,
  },
  {
    title: 'Category',
    url: '/fundraising/categories',
    icon: Layers3,
  },
  {
    title: 'Settings',
    url: '/fundraising/settings',
    icon: Settings,
  },
]

const salesNavigation = [
  {
    title: 'Sales',
    url: '/sales/dashboard',
    icon: LayoutDashboard,
  },
  {
    title: 'Enquiries',
    url: '/sales/enquiries',
    icon: FileText,
  },
  {
    title: 'Quotations',
    url: '/services/quotations',
    icon: FileText,
  },
  {
    title: 'Bookings',
    url: '/services/orders',
    icon: ShoppingCart,
  },
  {
    title: 'Customers',
    url: '/services/customers',
    icon: Users,
  },
  {
    title: 'Follow Ups',
    url: '/sales/follow-ups',
    icon: Calendar,
  },
  {
    title: 'Payments',
    url: '/finance/invoice-payments',
    icon: Receipt,
  },
  {
    title: 'WhatsApp',
    url: '/services/whatsapp',
    icon: MessageCircle,
  },
] as const

type SidebarNavigationItem = {
  title: string
  url: string
  icon: typeof LayoutDashboard
}

const organizationSidebarFeatureOrder = [
  'can_view_business_dashboard',
  'can_view_organization_tree',
  'can_view_branch_directory',
  'can_view_tenant_directory',
  'can_manage_in_apps',
  'can_access_announcements',
  'can_view_retail_customer_directory',
  'can_view_business_customer_directory',
  'can_view_client_directory',
  'can_view_user_directory',
  'can_view_role_directory',
  'can_view_vendor_directory',
  'can_view_supplier_directory',
] as const

const serviceOrganizationSidebarFeatureOrder = [
  'can_view_organization_tree',
  'can_view_branch_directory',
  'can_view_tenant_directory',
  'can_manage_in_apps',
  'can_access_announcements',
  'can_view_retail_customer_directory',
  'can_view_business_customer_directory',
  'can_view_client_directory',
  'can_view_user_directory',
  'can_view_role_directory',
  'can_view_vendor_directory',
  'can_view_supplier_directory',
] as const

const buildOrganizationSidebarItemFromFeature = (
  sidebarKey: string,
  isBranchWorkspace: boolean,
  canShowBranchAndInAppsItems: boolean,
  canShowOrganizationWorkspaceItem: boolean,
  labelOverrides: Record<string, string> = {},
): SidebarNavigationItem | null => {
  switch (sidebarKey) {
    case 'can_view_business_dashboard':
      return canShowBranchAndInAppsItems ? organisationNavigation[0] : null
    case 'can_view_organization_tree':
      return isBranchWorkspace
        ? { title: 'My Branch', url: '/org/branch', icon: GitBranch }
        : canShowOrganizationWorkspaceItem
          ? { title: 'My Organisation', url: '/org/organization', icon: Building2 }
          : null
    case 'can_view_branch_directory':
      if (!canShowBranchAndInAppsItems) {
        return null
      }
      return labelOverrides.levelDirectory
        ? { title: labelOverrides.levelDirectory, url: '/org/service-instance', icon: GitBranch }
        : null
    case 'can_view_tenant_directory':
      return labelOverrides.parentDirectory
        ? { title: labelOverrides.parentDirectory, url: '/org/tenants', icon: Landmark }
        : null
    case 'can_manage_in_apps':
      return SHOW_IN_APPS_FEATURE && canShowBranchAndInAppsItems
        ? { title: 'In-Apps', url: '/org/in-apps', icon: Package2 }
        : null
    case 'can_access_announcements':
      return marketingNavigationItem
    case 'can_view_vendor_directory':
      return null
    case 'can_view_retail_customer_directory':
      return null
    case 'can_view_business_customer_directory':
      return null
    case 'can_view_client_directory':
      return null
    case 'can_view_user_directory':
      return { title: 'Users', url: '/org/users', icon: Users }
    case 'can_view_supplier_directory':
      return null
    case 'can_view_role_directory':
      return organisationSupportNavigation[0]
    default:
      return null
  }
}

const roleDirectoryCapabilityKeys = ['can_view_role_directory', 'can_list_role_assignments'] as const
const branchDirectoryCapabilityKeys = [
  'can_view_branch_directory',
  'can_access_branch_management',
  'can_create_branches',
  'can_change_branch_admin',
  'can_manage_branch_state',
  'can_remove_branches',
] as const

const sidebarKeyAllowedFromPolicyAndCapabilities = (
  sidebarKey: string,
  selectedFeatureKeys: Set<string>,
  capabilities: SidebarCapabilities | undefined,
): boolean => {
  if (selectedFeatureKeys.has(sidebarKey)) {
    return true
  }
  const cap = capabilities as Record<string, boolean> | undefined
  if (sidebarKey === 'can_view_branch_directory') {
    return branchDirectoryCapabilityKeys.some((key) => Boolean(cap?.[key]))
  }
  return Boolean(cap?.[sidebarKey])
}

const buildOrganizationSidebarItemsFromFeatures = (
  features: PolicyFeature[],
  isOrganizationWorkspace: boolean,
  isBranchWorkspace: boolean,
  canShowBranchAndInAppsItems: boolean,
  canShowOrganizationWorkspaceItem: boolean,
  forceWorkspaceTreeItem: boolean,
  canManageRoles: boolean,
  isRootOrganizationWorkspace: boolean,
  capabilities: SidebarCapabilities | undefined,
  labelOverrides: Record<string, string> = {},
): SidebarNavigationItem[] => {
  const selectedFeatureKeys = new Set(
    features
      .filter((feature) => feature.selected === true && typeof feature.sidebarKey === 'string' && feature.sidebarKey.length > 0)
      .map((feature) => feature.sidebarKey as string),
  )

  const featureOrder = isOrganizationWorkspace
    ? organizationSidebarFeatureOrder
    : serviceOrganizationSidebarFeatureOrder

  const items = featureOrder.reduce<SidebarNavigationItem[]>((result, sidebarKey) => {
    if (!sidebarKeyAllowedFromPolicyAndCapabilities(sidebarKey, selectedFeatureKeys, capabilities)) {
      return result
    }

    const item = buildOrganizationSidebarItemFromFeature(
      sidebarKey,
      isBranchWorkspace,
      canShowBranchAndInAppsItems,
      canShowOrganizationWorkspaceItem,
      labelOverrides,
    )
    if (item) {
      result.push(item)
    }

    return result
  }, [])

  if (forceWorkspaceTreeItem) {
    const workspaceTreeUrl = isBranchWorkspace ? '/org/branch' : '/org/organization'
    const hasWorkspaceTreeItem = items.some((item) => item.url === workspaceTreeUrl)
    if (!hasWorkspaceTreeItem) {
      items.unshift(
        isBranchWorkspace
          ? { title: 'My Branch', url: '/org/branch', icon: GitBranch }
          : { title: 'My Organisation', url: '/org/organization', icon: Building2 },
      )
    }
  }

  const hasRoleDirectoryForFeaturesNav = sidebarKeyAllowedFromPolicyAndCapabilities(
    'can_view_role_directory',
    selectedFeatureKeys,
    capabilities,
  ) || roleDirectoryCapabilityKeys.some((key) => {
    const cap = capabilities as Record<string, boolean> | undefined
    return Boolean(cap?.[key])
  })

  if (isRootOrganizationWorkspace && hasRoleDirectoryForFeaturesNav && canManageRoles) {
    items.push(organisationSupportNavigation[1])
  }

  return items
}

const resolveRoleLabel = (
  currentRole: ReturnType<typeof useAuthStore.getState>['currentPost'],
  userRole?: string,
) => {
  const label = [
    currentRole?.roles_data?.roles_name,
    currentRole?.role_name,
    userRole,
  ].find((value) => typeof value === 'string' && value.trim().length > 0)

  return label?.trim() ?? ''
}

const resolveCurrentRoleId = (
  currentRole: ReturnType<typeof useAuthStore.getState>['currentPost'],
) => currentRole?.role_id || currentRole?.roles_data?.id || null

const resolveCurrentRoleCacheKey = (
  currentRole: ReturnType<typeof useAuthStore.getState>['currentPost'],
) => currentRole?.roles_data?.unique_id || resolveCurrentRoleId(currentRole)

type SidebarCapabilities = NonNullable<ReturnType<typeof useAuthStore.getState>['currentPost']>['capabilities']

type SidebarSubNavigationItem = {
  title: string
  url: string
}

type SidebarLeafNavigationEntry = {
  title: string
  url: string
  icon: typeof LayoutDashboard
  items?: never
}

type SidebarGroupNavigationEntry = {
  title: string
  icon: typeof LayoutDashboard
  items: readonly SidebarSubNavigationItem[]
  url?: never
}

type SidebarNavigationEntry = SidebarLeafNavigationEntry | SidebarGroupNavigationEntry

const hasNavigationUrl = <T extends { url?: string }>(item: T): item is T & { url: string } => (
  typeof item.url === 'string' && item.url.length > 0
)

const filterSidebarEntries = <T extends SidebarNavigationEntry>(
  entries: readonly T[],
  currentRole: ReturnType<typeof useAuthStore.getState>['currentPost'],
): T[] => entries
  .map((entry) => {
    if (entry.items) {
      const items = entry.items.filter((item) => evaluateRouteAccess(item.url.split('?')[0], currentRole).authorized)
      return items.length > 0 ? { ...entry, items } as T : null
    }

    return evaluateRouteAccess(entry.url.split('?')[0], currentRole).authorized ? entry : null
  })
  .filter((entry): entry is T => Boolean(entry))

const hasAnyCapability = (
  capabilities: SidebarCapabilities | undefined,
  capabilityNames: string[],
) => capabilityNames.some((capabilityName) => Boolean(capabilities?.[capabilityName as keyof NonNullable<typeof capabilities>]))

export function AppSidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const isPendingUser = isPendingRegistrationStatus(user?.registration_status)
  const currentBranch = useAuthStore((state) => state.currentBranch)
  const currentRole = useAuthStore((state) => state.currentPost)
  const availableRoles = useAuthStore((state) => state.availablePosts) || []
  const availableBranches = useAuthStore((state) => state.availableBranches)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const setServiceInstanceId = useAuthStore((state) => state.setServiceInstanceId)
  const accessToken = useAuthStore((state) => state.accessToken)
  const isBranchWorkspaceRoute = location.pathname === '/org/branch'
  const baseServiceInstanceId = resolveActiveServiceInstanceId({
    search: location.search,
    post: currentRole,
    currentBranch,
    availableBranches,
    storedServiceInstanceId,
    token: accessToken,
  })
  const workspaceNodeIdFromPost = String(currentRole?.node_id ?? '').trim()
  const currentRoleCacheKey = resolveCurrentRoleCacheKey(currentRole)
  const [pinnedWorkspaceNodeIds, setPinnedWorkspaceNodeIds] = useState<string[]>([])
  const currentRoleLabel = resolveRoleLabel(currentRole, user?.role)

  useEffect(() => {
    setPinnedWorkspaceNodeIds([])
  }, [currentRoleCacheKey])

  const activeOrganizationBootstrapQuery = useQuery({
    queryKey: orgServiceInstanceLightQueryKey(baseServiceInstanceId!),
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(baseServiceInstanceId!),
    enabled: Boolean(baseServiceInstanceId) && !isBranchWorkspaceRoute,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
    retry: false,
  })
  const bootstrapPrimaryNodeId = String(activeOrganizationBootstrapQuery.data?.primary_node ?? '').trim()
  const workspaceNodeCandidates = useMemo(
    () => Array.from(new Set([
      workspaceNodeIdFromPost,
      bootstrapPrimaryNodeId,
    ].filter(Boolean))),
    [bootstrapPrimaryNodeId, workspaceNodeIdFromPost],
  )
  const primaryWorkspaceNodeId = workspaceNodeIdFromPost || bootstrapPrimaryNodeId
  const effectiveWorkspaceNodeIds = useMemo(
    () => {
      if (isBranchWorkspaceRoute) {
        return primaryWorkspaceNodeId ? [primaryWorkspaceNodeId] : []
      }

      return Array.from(new Set([
        ...workspaceNodeCandidates,
        ...pinnedWorkspaceNodeIds,
      ]))
    },
    [isBranchWorkspaceRoute, pinnedWorkspaceNodeIds, primaryWorkspaceNodeId, workspaceNodeCandidates],
  )
  const workspaceSelectionHint = useMemo(
    () => getServiceInstanceIdFromSearch(location.search)
      || String(storedServiceInstanceId ?? '').trim()
      || baseServiceInstanceId,
    [baseServiceInstanceId, location.search, storedServiceInstanceId],
  )
  const postDerivedWorkspaceOptions = useMemo(
    () => mergeNodeServicePermissionOptions(
      ...effectiveWorkspaceNodeIds.map((nodeId) => (
        buildNodeServicePermissionOptionsFromPosts(availableRoles, nodeId)
      )),
    ),
    [availableRoles, effectiveWorkspaceNodeIds],
  )
  const nodeLinkedServiceInstances = useNodeLinkedServiceInstances({
    nodeIds: effectiveWorkspaceNodeIds,
    activeServiceInstanceId: workspaceSelectionHint,
    enabled: effectiveWorkspaceNodeIds.length > 0,
    supplementalOptions: postDerivedWorkspaceOptions,
  })

  useEffect(() => {
    if (isBranchWorkspaceRoute) {
      return
    }

    if (!nodeLinkedServiceInstances.hasMultiple || workspaceNodeCandidates.length === 0) {
      return
    }

    setPinnedWorkspaceNodeIds((previous) => {
      const merged = Array.from(new Set([...previous, ...workspaceNodeCandidates]))
      if (merged.length === previous.length && merged.every((value, index) => value === previous[index])) {
        return previous
      }
      return merged
    })
  }, [isBranchWorkspaceRoute, nodeLinkedServiceInstances.hasMultiple, workspaceNodeCandidates])
  const nodeLinkedServiceInstancesWithActiveNode = useNodeLinkedServiceInstances({
    nodeIds: Array.from(new Set([
      ...effectiveWorkspaceNodeIds,
      bootstrapPrimaryNodeId,
    ].filter(Boolean))),
    activeServiceInstanceId: workspaceSelectionHint,
    enabled: !isBranchWorkspaceRoute && (effectiveWorkspaceNodeIds.length > 0 || Boolean(bootstrapPrimaryNodeId)),
    supplementalOptions: postDerivedWorkspaceOptions,
  })
  const workspaceNodeLinkedServiceInstances = isBranchWorkspaceRoute
    ? nodeLinkedServiceInstances
    : (nodeLinkedServiceInstancesWithActiveNode.instances.length
      >= nodeLinkedServiceInstances.instances.length
      ? nodeLinkedServiceInstancesWithActiveNode
      : nodeLinkedServiceInstances)
  const workspaceNodeInstanceIds = useMemo(
    () => getNodeLinkedServiceInstanceIds(workspaceNodeLinkedServiceInstances.instances),
    [workspaceNodeLinkedServiceInstances.instances],
  )

  useEffect(() => {
    if (isBranchWorkspaceRoute) {
      return
    }

    if (!workspaceNodeLinkedServiceInstances.hasMultiple) {
      return
    }

    const candidates = Array.from(new Set([
      ...workspaceNodeCandidates,
      bootstrapPrimaryNodeId,
    ].filter(Boolean)))

    if (candidates.length === 0) {
      return
    }

    setPinnedWorkspaceNodeIds((previous) => {
      const merged = Array.from(new Set([...previous, ...candidates]))
      if (merged.length === previous.length && merged.every((value, index) => value === previous[index])) {
        return previous
      }
      return merged
    })
  }, [
    bootstrapPrimaryNodeId,
    isBranchWorkspaceRoute,
    workspaceNodeCandidates,
    workspaceNodeLinkedServiceInstances.hasMultiple,
  ])
  const serviceInstanceId = useMemo(
    () => resolveWorkspaceActiveServiceInstanceId({
      search: location.search,
      post: currentRole,
      currentBranch,
      availableBranches,
      storedServiceInstanceId,
      token: accessToken,
      nodeInstanceIds: workspaceNodeInstanceIds,
    }) || baseServiceInstanceId,
    [
      availableBranches,
      baseServiceInstanceId,
      currentBranch,
      currentRole,
      location.search,
      storedServiceInstanceId,
      accessToken,
      workspaceNodeInstanceIds,
    ],
  )
  const fundraisingServiceInstanceId = useMemo(
    () => resolveFundraisingScopedServiceInstanceId({
      search: location.search,
      post: currentRole,
      availablePosts: availableRoles,
      storedServiceInstanceId,
    }) || serviceInstanceId,
    [availableRoles, currentRole, location.search, serviceInstanceId, storedServiceInstanceId],
  )
  const sidebarScopedServiceInstanceId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const viewedId = String(params.get('id') ?? '').trim()
    const isSettingsRoute =
      location.pathname.startsWith('/org/service-instrance/form')
      || location.pathname.startsWith('/org/service-instance/form')
      || location.pathname.startsWith('/organization/service-instrance/form')
      || location.pathname.startsWith('/organization/service-instance/form')
      || location.pathname.startsWith('/org/node/service-instrance/config-form')

    if (isSettingsRoute && viewedId) {
      return viewedId
    }

    return serviceInstanceId
  }, [location.pathname, location.search, serviceInstanceId])

  const activeOrganizationQuery = useQuery({
    queryKey: orgServiceInstanceLightQueryKey(sidebarScopedServiceInstanceId!),
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(sidebarScopedServiceInstanceId!),
    enabled: Boolean(sidebarScopedServiceInstanceId) && !isBranchWorkspaceRoute,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
    retry: false,
  })
  const activeOrganizationPrimaryNodeId = String(activeOrganizationQuery.data?.primary_node ?? '').trim()

  useEffect(() => {
    if (!workspaceNodeLinkedServiceInstances.hasMultiple || !activeOrganizationPrimaryNodeId) {
      return
    }

    setPinnedWorkspaceNodeIds((previous) => {
      if (previous.includes(activeOrganizationPrimaryNodeId)) {
        return previous
      }
      return [...previous, activeOrganizationPrimaryNodeId]
    })
  }, [activeOrganizationPrimaryNodeId, workspaceNodeLinkedServiceInstances.hasMultiple])

  const workspaceContextPost = useMemo(
    () => resolveWorkspacePostContext({
      post: currentRole,
      availablePosts: availableRoles,
      serviceInstanceId,
      nodeInstances: workspaceNodeLinkedServiceInstances.instances,
    }) ?? currentRole,
    [
      availableRoles,
      currentRole,
      workspaceNodeLinkedServiceInstances.instances,
      serviceInstanceId,
    ],
  )
  const postVariantIdentity = readServiceVariantIdentity(workspaceContextPost ?? undefined)
  const currentVariantUniqueId = postVariantIdentity.uniqueId
  const currentVariantLabel = postVariantIdentity.label
  const currentRoleVariantIdentity = readServiceVariantIdentity(currentRole ?? undefined)
  const currentRoleVariantUniqueId = currentRoleVariantIdentity.uniqueId
  const currentRoleVariantLabel = currentRoleVariantIdentity.label
  const currentRoleIsOrganizationScope = isOrganizationServiceVariant(
    currentRoleVariantUniqueId,
    currentRoleVariantLabel,
  )
  const currentRoleIsServicesProduct = isServicesProductVariant(
    currentRoleVariantUniqueId,
    currentRoleVariantLabel,
  )
  const isServiceBranchTypeWorkspace = isBranchOperatorWorkspaceContextFromPost(currentRole)
  const workspaceContextServiceInstanceId = String(
    workspaceContextPost?.service_instance
      ?? workspaceContextPost?.service_instance_id
      ?? '',
  ).trim()
  const currentRoleServiceInstanceId = String(
    currentRole?.service_instance
      ?? currentRole?.service_instance_id
      ?? '',
  ).trim()
  const tenantWorkspaceServiceInstanceId = useMemo(() => {
    const tenantPost = availableRoles.find((post) => (
      post?.level_variant_unique_id === 'head-tenant-lvl-variant'
      || String(post?.service_variant_type ?? '').trim().toUpperCase() === 'TENANT'
      || String(post?.service_variant_name ?? '').trim().toUpperCase() === 'TENANT'
    ))

    return String(
      tenantPost?.service_instance
      ?? tenantPost?.service_instance_id
      ?? '',
    ).trim()
  }, [availableRoles])
  const isTenantRouteContext = location.pathname.startsWith('/org/tenants')
    || location.pathname.startsWith('/organization/tenants')
  const isTenantWorkspaceContext = isTenantOrganizationServiceVariant(
    currentRoleVariantUniqueId,
    currentRoleVariantLabel,
  ) || isTenantRouteContext
  const isBranchWorkspaceContextForRestriction = isServiceBranchTypeWorkspace
  const currentRoleIsRootOrganizationScope = isRootOrganizationServiceVariant(
    currentRoleVariantUniqueId,
    currentRoleVariantLabel,
  )
  const shouldRestrictForNonBranchServicesWorkspace = currentRoleIsServicesProduct
    && !isBranchWorkspaceContextForRestriction
    && !currentRoleIsOrganizationScope
    && !currentRoleIsRootOrganizationScope
    && !isTenantWorkspaceContext
  const shouldRestrictWorkspaceOptionsToCurrentInApp = isTenantWorkspaceContext
    || shouldRestrictForNonBranchServicesWorkspace
  const restrictedWorkspaceServiceInstanceId = shouldRestrictWorkspaceOptionsToCurrentInApp
    ? (
      isTenantWorkspaceContext
        ? (
          tenantWorkspaceServiceInstanceId
          || workspaceContextServiceInstanceId
          || String(serviceInstanceId ?? '').trim()
        )
        : (
          workspaceContextServiceInstanceId
          || currentRoleServiceInstanceId
          || String(serviceInstanceId ?? '').trim()
        )
    )
    : ''
  const workspaceServiceOptions = useMemo(() => {
    const options = workspaceNodeLinkedServiceInstances.serviceOptions
    if (!shouldRestrictWorkspaceOptionsToCurrentInApp || !restrictedWorkspaceServiceInstanceId) {
      return options
    }

    const currentInAppOption = options.find(
      (option) => String(option.instanceId ?? '').trim() === restrictedWorkspaceServiceInstanceId,
    )
    if (currentInAppOption) {
      return [currentInAppOption]
    }

    const selectedOption = resolveNodeServicePermissionOption(options, serviceInstanceId)
    return selectedOption ? [selectedOption] : options
  }, [
    currentRoleServiceInstanceId,
    currentRoleIsOrganizationScope,
    currentRoleIsRootOrganizationScope,
    currentRoleIsServicesProduct,
    currentRoleVariantLabel,
    currentRoleVariantUniqueId,
    isBranchWorkspaceContextForRestriction,
    isTenantWorkspaceContext,
    serviceInstanceId,
    restrictedWorkspaceServiceInstanceId,
    shouldRestrictForNonBranchServicesWorkspace,
    shouldRestrictWorkspaceOptionsToCurrentInApp,
    tenantWorkspaceServiceInstanceId,
    workspaceContextServiceInstanceId,
    workspaceNodeLinkedServiceInstances.serviceOptions,
  ])
  const hasMultipleWorkspaceOptions = workspaceServiceOptions.length > 1
  const shouldShowWorkspaceSelector = shouldRestrictWorkspaceOptionsToCurrentInApp
    ? hasMultipleWorkspaceOptions
    : (hasMultipleWorkspaceOptions || workspaceNodeLinkedServiceInstances.hasMultiple)
  const selectedVisibleWorkspaceServiceOption = useMemo(
    () => resolveNodeServicePermissionOption(workspaceServiceOptions, serviceInstanceId),
    [serviceInstanceId, workspaceServiceOptions],
  )
  const currentVariantLabelRaw = mapCustomerFacingServicesTerm(currentVariantLabel)
  const isOrganizationWorkspace = isOrganizationServiceVariant(currentVariantUniqueId, currentVariantLabel)
  const isTenantWorkspace = isTenantWorkspaceContext
  const isRootOrganizationWorkspace = isRootOrganizationServiceVariant(currentVariantUniqueId, currentVariantLabel)
  const roleScopedActiveName = isOrganizationWorkspace
    ? workspaceContextPost?.org_name
    : workspaceContextPost?.service_instance_name
  const activeOrganizationDirectLogo = resolveSidebarOrganizationLogo(activeOrganizationQuery.data)
  const activeOrganizationParentInstanceId = String(activeOrganizationQuery.data?.parent_instance_id ?? '').trim()

  const activeOrganizationVariantIdentity = readServiceVariantIdentity(activeOrganizationQuery.data ?? undefined)
  const activeOrganizationVariantUniqueId = activeOrganizationVariantIdentity.uniqueId
  const activeOrganizationVariantLabel = activeOrganizationVariantIdentity.label

  const branchServicesPost = useMemo(() => {
    if (!workspaceContextPost || !isInAppChildServiceVariant(currentVariantUniqueId, currentVariantLabel)) {
      return null
    }

    if (!activeOrganizationParentInstanceId) {
      return null
    }

    return availableRoles.find((post) => {
      if (!isServicesProductVariant(
        post.service_variant_unique_id,
        post.service_variant_name ?? post.service_variant_type,
      )) {
        return false
      }

      const postInstanceId = String(post.service_instance ?? post.service_instance_id ?? '').trim()
      return postInstanceId === activeOrganizationParentInstanceId
    }) ?? null
  }, [
    activeOrganizationParentInstanceId,
    availableRoles,
    workspaceContextPost,
    currentVariantLabel,
    currentVariantUniqueId,
  ])

  // Workspace sections: derive from the active service instance context, with org-instance fallback.
  const isBranchWorkspace = isServiceBranchTypeWorkspace
    || isBranchOperatorWorkspaceContext(activeOrganizationQuery.data)
  const isServicesWorkspace = isServicesProductVariant(currentVariantUniqueId, currentVariantLabel)
    || isServicesProductVariant(activeOrganizationVariantUniqueId, activeOrganizationVariantLabel)
  const isMembershipWorkspace = isMembershipServiceVariant(currentVariantUniqueId, currentVariantLabel)
    || isMembershipServiceVariant(activeOrganizationVariantUniqueId, activeOrganizationVariantLabel)
  const isFundraisingWorkspace = [
    currentVariantUniqueId,
    currentVariantLabel,
    activeOrganizationVariantUniqueId,
    activeOrganizationVariantLabel,
    currentVariantLabelRaw,
    workspaceContextPost?.service_variant_name,
    workspaceContextPost?.service_variant_type,
    workspaceContextPost?.service_variant_service,
    activeOrganizationQuery.data?.service_variant_name,
    activeOrganizationQuery.data?.service_variant_label,
    activeOrganizationQuery.data?.service_variant_service,
  ].some(isFundraisingContextValue)
  const isVenueWorkspace = [
    currentVariantUniqueId,
    currentVariantLabel,
    activeOrganizationVariantUniqueId,
    activeOrganizationVariantLabel,
    currentVariantLabelRaw,
    workspaceContextPost?.service_variant_name,
    workspaceContextPost?.service_variant_type,
    workspaceContextPost?.service_variant_service,
    activeOrganizationQuery.data?.service_variant_name,
    activeOrganizationQuery.data?.service_variant_label,
    activeOrganizationQuery.data?.service_variant_service,
  ].some(isVenueContextValue)
  const isActiveEventWorkspace = useMemo(() => {
    const activeSignals = [
      currentVariantUniqueId,
      currentVariantLabel,
      currentVariantLabelRaw,
      workspaceContextPost?.service_variant_name,
      workspaceContextPost?.service_variant_type,
      workspaceContextPost?.service_variant_service,
      activeOrganizationVariantUniqueId,
      activeOrganizationVariantLabel,
      activeOrganizationQuery.data?.service_variant_name,
      activeOrganizationQuery.data?.service_variant_label,
      activeOrganizationQuery.data?.service_variant_service,
    ]

    return activeSignals.some(isEventContextValue)
  }, [
    activeOrganizationQuery.data?.service_variant_label,
    activeOrganizationQuery.data?.service_variant_name,
    activeOrganizationQuery.data?.service_variant_service,
    activeOrganizationVariantLabel,
    activeOrganizationVariantUniqueId,
    currentVariantLabel,
    currentVariantLabelRaw,
    currentVariantUniqueId,
    workspaceContextPost?.service_variant_name,
    workspaceContextPost?.service_variant_service,
    workspaceContextPost?.service_variant_type,
  ])
  const hasProjectInApps = useMemo(() => {
    const optionSignals = workspaceNodeLinkedServiceInstances.serviceOptions.flatMap((option) => [
      option.variantId,
      option.variantLabel,
    ])

    const instanceSignals = workspaceNodeLinkedServiceInstances.instances.flatMap((instance) => [
      instance.service_variant,
      instance.service_variant_name,
      instance.service_variant_label,
      instance.service_variant_type,
      instance.service_data?.variant_unique,
      instance.service_data?.service_name,
      instance.type,
    ])

    return [...optionSignals, ...instanceSignals].some(isProjectContextValue)
  }, [workspaceNodeLinkedServiceInstances.instances, workspaceNodeLinkedServiceInstances.serviceOptions])
  const isOperationalWorkspace = isBranchWorkspace || isServicesWorkspace

  // When viewing an in-app child post, borrow the parent branch Services post for capability/policy lookup only.
  const capabilitySourcePost = branchServicesPost ?? currentRole

  const activeOrganizationLogoFallbackQuery = useQuery({
    queryKey: ['sidebar-active-service-instance-logo-fallback', activeOrganizationParentInstanceId, sidebarScopedServiceInstanceId],
    queryFn: async () => {
      const directParent = activeOrganizationParentInstanceId && activeOrganizationParentInstanceId !== sidebarScopedServiceInstanceId
        ? await orgTreeApi.resolvePublicSignupServiceInstance(activeOrganizationParentInstanceId)
        : null

      if (resolveSidebarOrganizationLogo(directParent) || !sidebarScopedServiceInstanceId) {
        return directParent
      }

      return orgTreeApi.findAncestorRoot(sidebarScopedServiceInstanceId)
    },
    enabled: Boolean(!activeOrganizationDirectLogo && sidebarScopedServiceInstanceId && (activeOrganizationParentInstanceId || activeOrganizationQuery.data)),
    staleTime: 60_000,
    placeholderData: (previous) => previous,
    retry: false,
  })
  const activeOrganizationLogo = activeOrganizationDirectLogo
    || resolveSidebarOrganizationLogo(activeOrganizationLogoFallbackQuery.data)
    || currentRole?.service_instance_logo
    || currentRole?.org_logo
    || null
  const [stableActiveOrganizationLogo, setStableActiveOrganizationLogo] = useState<string | null>(activeOrganizationLogo)

  useEffect(() => {
    setStableActiveOrganizationLogo(null)
  }, [sidebarScopedServiceInstanceId])

  useEffect(() => {
    if (activeOrganizationLogo) {
      setStableActiveOrganizationLogo(activeOrganizationLogo)
      return
    }

    const activeQuerySettled = !activeOrganizationQuery.isFetching
    const fallbackQuerySettled = !activeOrganizationLogoFallbackQuery.isFetching
    const fallbackQueryDisabled = !activeOrganizationLogoFallbackQuery.isEnabled
    if (activeQuerySettled && (fallbackQuerySettled || fallbackQueryDisabled)) {
      setStableActiveOrganizationLogo(null)
    }
  }, [
    activeOrganizationLogo,
    activeOrganizationLogoFallbackQuery.isEnabled,
    activeOrganizationLogoFallbackQuery.isFetching,
    activeOrganizationQuery.isFetching,
  ])
  const visibleActiveOrganizationLogo = activeOrganizationLogo || stableActiveOrganizationLogo
  const activeOrganizationName = activeOrganizationQuery.data?.name
    || roleScopedActiveName
    || currentBranch?.name
    || (isOrganizationWorkspace ? 'Organization context' : 'No branch selected')
  const workspaceVariantDisplayLabel = useMemo(() => {
    if (selectedVisibleWorkspaceServiceOption?.variantLabel) {
      return selectedVisibleWorkspaceServiceOption.variantLabel
    }

    if (activeOrganizationQuery.data) {
      return getServiceInstanceVariantDisplayName(activeOrganizationQuery.data)
    }

    return currentVariantLabel || null
  }, [
    activeOrganizationQuery.data,
    currentVariantLabel,
    selectedVisibleWorkspaceServiceOption?.variantLabel,
  ])
  const activeOrganizationStatusLabel = getServiceRegistrationStatusLabel(activeOrganizationQuery.data?.status)
  const homeCardHref = selectedVisibleWorkspaceServiceOption
    ? buildServiceVariantHomePathFromVariant(
      selectedVisibleWorkspaceServiceOption.variantId,
      selectedVisibleWorkspaceServiceOption.variantLabel,
      serviceInstanceId,
    )
    : buildServiceVariantHomePath(workspaceContextPost, serviceInstanceId)

  const handleWorkspaceInstanceSelect = (option: NodeServicePermissionOption) => {
    if (!option.instanceId || option.instanceId === serviceInstanceId) {
      return
    }

    setServiceInstanceId(option.instanceId)
    queryClient.invalidateQueries({ queryKey: ['sidebar-active-service-instance'] })
    queryClient.invalidateQueries({ queryKey: ['sidebar-policy-features'] })
    const destination = buildServiceVariantHomePathFromVariant(
      option.variantId,
      option.variantLabel,
      option.instanceId,
    )

    navigate(destination, { replace: true })
  }

  const sidebarLevelVariantServiceInstanceId = sidebarScopedServiceInstanceId || serviceInstanceId
  const sidebarLevelVariantsQuery = useQuery({
    queryKey: ['sidebar-level-variants', sidebarLevelVariantServiceInstanceId],
    queryFn: () => orgLevelVariantsApi.list(sidebarLevelVariantServiceInstanceId!),
    enabled: Boolean(sidebarLevelVariantServiceInstanceId) && !isBranchWorkspaceRoute,
    staleTime: 60_000,
    retry: false,
  })

  const currentRoleLevelVariantUniqueId = readPostLevelVariantUniqueId(
    workspaceContextPost,
    activeOrganizationQuery.data,
  )

  const childLevelVariants = useMemo(() => {
    // 1) level variant unique id comes from current post role data
    // 2) null/empty means do not process or render child levels
    const currentLevelVariantUniqueId = currentRoleLevelVariantUniqueId
    if (!currentLevelVariantUniqueId) {
      return []
    }

    const rows = sidebarLevelVariantsQuery.data ?? []
    if (!rows.length) {
      return []
    }

    // 3) traverse list and find direct children of matching level-variant unique id
    const currentLevelVariant = rows.find(
      (row) => String(row.unique_id ?? '').trim() === currentLevelVariantUniqueId,
    )
    if (!currentLevelVariant) {
      return []
    }

    const currentLevelVariantId = Number(currentLevelVariant.id)
    if (!Number.isFinite(currentLevelVariantId) || currentLevelVariantId === 0) {
      return []
    }

    const directChildren = rows.filter(
      (row) => Number(row.parent_id) === currentLevelVariantId && Number(row.active) !== 0,
    )

    const currentUniqueId = String(currentLevelVariant.unique_id ?? '').trim()
    const explicitAllowedChildren = getDefaultChildLevelVariantUniqueIds(currentUniqueId)

    const children = directChildren.length > 0
      ? directChildren
      : explicitAllowedChildren
        ? rows.filter((row) => explicitAllowedChildren.has(String(row.unique_id ?? '').trim()) && Number(row.active) !== 0)
        : []

    return children
      .map((row) => ({
        id: row.id,
        uniqueId: String(row.unique_id || '').trim(),
        targetPath: resolveLevelVariantDirectoryPath(row),
        name: readLevelVariantDisplayName(row),
        sortOrder: Number(row.sort_order ?? Number.MAX_SAFE_INTEGER),
      }))
      .sort((left, right) => {
        if (left.sortOrder !== right.sortOrder) {
          return left.sortOrder - right.sortOrder
        }
        return left.name.localeCompare(right.name)
      })
  }, [currentRoleLevelVariantUniqueId, sidebarLevelVariantsQuery.data])

  const levelVariantMeta = useMemo(() => {
    const rows = sidebarLevelVariantsQuery.data
    if (!rows) {
      return {
        isResolved: false,
        directoryUniqueId: null as string | null,
        parentDirectoryUniqueId: null as string | null,
        labelOverrides: {} as Record<string, string>,
      }
    }

    const activeRows = rows.filter((row) => Number(row.active) !== 0)
    const sortedRows = [...activeRows].sort((left, right) => {
      const leftOrder = Number(left.sort_order ?? Number.MAX_SAFE_INTEGER)
      const rightOrder = Number(right.sort_order ?? Number.MAX_SAFE_INTEGER)
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder
      }
      return Number(left.id) - Number(right.id)
    })

    const currentRoleLevelVariant = currentRoleLevelVariantUniqueId
      ? sortedRows.find((row) => String(row.unique_id ?? '').trim() === currentRoleLevelVariantUniqueId) ?? null
      : null

    const byId = new Map<number, (typeof activeRows)[number]>()
    const childCountByParentId = new Map<number, number>()
    for (const row of sortedRows) {
      byId.set(Number(row.id), row)
      const parentId = Number(row.parent_id)
      if (Number.isFinite(parentId)) {
        childCountByParentId.set(parentId, (childCountByParentId.get(parentId) ?? 0) + 1)
      }
    }

    const depthMemo = new Map<number, number>()
    const resolveDepth = (id: number): number => {
      if (depthMemo.has(id)) {
        return depthMemo.get(id) as number
      }
      const row = byId.get(id)
      if (!row) {
        depthMemo.set(id, 0)
        return 0
      }
      const parentId = Number(row.parent_id)
      const depth = Number.isFinite(parentId) ? resolveDepth(parentId) + 1 : 0
      depthMemo.set(id, depth)
      return depth
    }

    const directChildrenOfCurrentRoleLevel = currentRoleLevelVariant
      ? sortedRows.filter((row) => Number(row.parent_id) === Number(currentRoleLevelVariant.id) && Number(row.active) !== 0)
      : []
    const firstDirectChildLevelVariant = [...directChildrenOfCurrentRoleLevel].sort((left, right) => {
      const leftOrder = Number(left.sort_order ?? Number.MAX_SAFE_INTEGER)
      const rightOrder = Number(right.sort_order ?? Number.MAX_SAFE_INTEGER)
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder
      }
      return Number(left.id) - Number(right.id)
    })[0] ?? null

    const currentUniqueId = String(currentRoleLevelVariant?.unique_id ?? '').trim()
    const directoryLevelVariant = currentUniqueId === 'head-tenant-lvl-variant'
      || currentUniqueId === 'tenant-lvl-variant'
      ? findLevelVariantByUniqueId(sortedRows, 'branch-lvl-variant')
      : firstDirectChildLevelVariant
    const parentDirectoryLevelVariant = currentUniqueId === 'head-tenant-lvl-variant'
      ? findLevelVariantByUniqueId(sortedRows, 'tenant-lvl-variant')
      : null

    const directoryUniqueIdRaw = directoryLevelVariant
      ? String(directoryLevelVariant.unique_id ?? '').trim()
      : ''
    const parentDirectoryUniqueIdRaw = parentDirectoryLevelVariant
      ? String(parentDirectoryLevelVariant.unique_id ?? '').trim()
      : ''

    const labelOverrides: Record<string, string> = {}
    const directoryLabel = readLevelVariantDisplayName(directoryLevelVariant)
    if (directoryLabel) {
      labelOverrides.levelDirectory = directoryLabel
    }
    const parentDirectoryLabel = readLevelVariantDisplayName(parentDirectoryLevelVariant)
    if (parentDirectoryLabel) {
      labelOverrides.parentDirectory = parentDirectoryLabel
    }

    return {
      isResolved: true,
      directoryUniqueId: directoryUniqueIdRaw || null,
      directoryPath: resolveLevelVariantDirectoryPath(directoryLevelVariant),
      parentDirectoryUniqueId: parentDirectoryUniqueIdRaw || null,
      labelOverrides,
    }
  }, [currentRoleLevelVariantUniqueId, sidebarLevelVariantsQuery.data])

  const directoryLevelVariantHref = useMemo(() => {
    const directoryPath = levelVariantMeta.directoryPath || '/org/service-instance'
    if (!levelVariantMeta.directoryUniqueId) {
      return withServiceInstanceId(directoryPath, sidebarLevelVariantServiceInstanceId)
    }
    const params = new URLSearchParams()
    params.set('lvl_variant', levelVariantMeta.directoryUniqueId)
    params.set('unique_level_variant_id', levelVariantMeta.directoryUniqueId)
    return withServiceInstanceId(`${directoryPath}?${params.toString()}`, sidebarLevelVariantServiceInstanceId)
  }, [levelVariantMeta.directoryPath, levelVariantMeta.directoryUniqueId, sidebarLevelVariantServiceInstanceId])

  const tenantDirectoryLevelVariantHref = useMemo(() => {
    if (!levelVariantMeta.parentDirectoryUniqueId) {
      return withServiceInstanceId('/org/tenants', sidebarLevelVariantServiceInstanceId)
    }

    const params = new URLSearchParams()
    const parentUniqueId = levelVariantMeta.parentDirectoryUniqueId
    params.set('lvl_variant', parentUniqueId)
    params.set('unique_level_variant_id', parentUniqueId)
    return withServiceInstanceId(`/org/tenants?${params.toString()}`, sidebarLevelVariantServiceInstanceId)
  }, [levelVariantMeta.parentDirectoryUniqueId, sidebarLevelVariantServiceInstanceId])

  const myBranchSettingsHref = useMemo(() => {
    if (!sidebarScopedServiceInstanceId) {
      return null
    }

    const primaryNodeId = String(activeOrganizationQuery.data?.primary_node ?? '').trim()
    if (primaryNodeId) {
      const params = new URLSearchParams({
        action: 'edit',
        id: sidebarScopedServiceInstanceId,
        tab: 'details',
      })

      return withServiceInstanceId(
        buildOrgServiceInstanceFormPath(primaryNodeId, params),
        sidebarScopedServiceInstanceId,
      )
    }

    return withServiceInstanceId(`/org/service-instance/form/${sidebarScopedServiceInstanceId}?tab=details`, sidebarScopedServiceInstanceId)
  }, [activeOrganizationQuery.data?.primary_node, sidebarScopedServiceInstanceId])

  const sidebarScopeLabel = useMemo(() => {
    if (activeOrganizationQuery.data) {
      return getServiceInstanceScopeLabelFromInstance(activeOrganizationQuery.data)
    }

    if (isOrganizationWorkspace) {
      return 'Organisation'
    }

    if (isBranchWorkspace) {
      return 'Branch'
    }

    return currentVariantLabelRaw || 'App Instance'
  }, [
    activeOrganizationQuery.data,
    currentVariantLabelRaw,
    isBranchWorkspace,
    isOrganizationWorkspace,
  ])
  const capabilitySourceRoleId = resolveCurrentRoleId(capabilitySourcePost)
  const capabilitySourceRoleCacheKey = resolveCurrentRoleCacheKey(capabilitySourcePost)

  const organizationSidebarFeaturesQuery = useQuery({
    queryKey: ['sidebar-policy-features', capabilitySourceRoleCacheKey, serviceInstanceId],
    queryFn: () => policyFeaturesApi.listCurrentService({
      roleId: capabilitySourceRoleId!,
      serviceInstanceId: serviceInstanceId!,
      includeHiddenFeatures: true,
    }),
    enabled: Boolean(capabilitySourceRoleId && capabilitySourceRoleCacheKey && serviceInstanceId) && !isBranchWorkspaceRoute,
    staleTime: 60_000,
    retry: false,
  })

  // const sidebarFeatureRows = organizationSidebarFeaturesQuery.data?.features ?? []

  // Temporarily disabled while Project Management sidebar group is hidden.
  // const isSidebarFeatureExplicitlyDisabled = (keys: string[]) => {
  //   if (sidebarFeatureRows.length === 0) {
  //     return false
  //   }

  //   const matched = sidebarFeatureRows.filter((feature) => {
  //     const featureKey = String(feature.sidebarKey ?? '').trim()
  //     return featureKey.length > 0 && keys.includes(featureKey)
  //   })

  //   if (matched.length === 0) {
  //     return false
  //   }

  //   return matched.every((feature) => feature.selected !== true)
  // }

  // Temporarily disabled while Project Management sidebar group is hidden.
  // const hideProjectSidebarGroup = isSidebarFeatureExplicitlyDisabled([
  //   'can_access_projects',
  //   'can_access_job_projects',
  //   'can_manage_projects',
  // ])

  const sidebarUserGroupsQuery = useQuery({
    queryKey: ['sidebar-user-groups', serviceInstanceId],
    queryFn: () => orgUsersApi.listGroups({ serviceInstanceId }),
    enabled: Boolean(serviceInstanceId)
      && (
        location.pathname.startsWith('/org/users')
        || location.pathname.startsWith('/organization/users')
        || (!isBranchWorkspaceRoute && (isOrganizationWorkspace || isBranchWorkspace || !isTenantWorkspace))
      ),
    staleTime: 60_000,
    retry: false,
  })

  const sidebarGroupNavItems = useMemo(() => {
    const groups = (sidebarUserGroupsQuery.data ?? []).filter((group) => {
      const isDefaultUserGroup = String(group.unique_code ?? '').trim().toLowerCase() === 'user'
      return Boolean(group.show_in_sidebar) || isDefaultUserGroup
    })

    return groups.map((group) => {
      const params = new URLSearchParams()
      params.set('user_group', String(group.id))
      params.set('source', 'sidebar_user')
      return {
        title: String(group.title || group.unique_code || 'Users'),
        url: withServiceInstanceId(`/org/users?${params.toString()}`, serviceInstanceId),
        icon: Users,
      }
    })
  }, [sidebarUserGroupsQuery.data, serviceInstanceId])

  const effectiveCapabilities = useMemo(
    () => buildEffectiveCapabilities(capabilitySourcePost, organizationSidebarFeaturesQuery.data?.features),
    [capabilitySourcePost, organizationSidebarFeaturesQuery.data?.features],
  )

  const effectiveCurrentRole = useMemo(
    () => (workspaceContextPost ? { ...workspaceContextPost, capabilities: effectiveCapabilities } : workspaceContextPost),
    [workspaceContextPost, effectiveCapabilities],
  )

  const visibleMainNavigation = useMemo(
    () => filterSidebarEntries(navigation, effectiveCurrentRole),
    [effectiveCurrentRole],
  )

  const visibleAssetManagementNavigation = useMemo(() => {
    if (!isOperationalWorkspace) {
      return []
    }
    return filterSidebarEntries(assetManagementNavigation, effectiveCurrentRole)
  }, [effectiveCurrentRole, isOperationalWorkspace])

  const visibleProjectNavigation = useMemo(() => {
    if (!isOperationalWorkspace || !SHOW_PROJECT_MGMT_SIDEBAR || !hasProjectInApps) {
      return []
    }

    return filterSidebarEntries(projectNavigation, effectiveCurrentRole)
  }, [effectiveCurrentRole, hasProjectInApps, isOperationalWorkspace])

  const visibleEventNavigation = useMemo(() => {
    // Keep Event Management top-level, but only in the active Event in-app workspace.
    if (!SHOW_EVENT_MGMT_SIDEBAR || !isActiveEventWorkspace) {
      return []
    }

    return filterSidebarEntries(eventNavigation, effectiveCurrentRole)
  }, [effectiveCurrentRole, isActiveEventWorkspace])

  const visibleFinanceNavigation = useMemo(() => {
    if (!isOperationalWorkspace) {
      return []
    }

    if (effectiveCapabilities?.can_access_finance === false) {
      return []
    }

    return filterSidebarEntries(financeNavigation, effectiveCurrentRole)
  }, [effectiveCapabilities?.can_access_finance, effectiveCurrentRole, isOperationalWorkspace])

  const visibleStoreNavigation = useMemo<SidebarNavigationEntry[]>(() => {
    if (!isOperationalWorkspace || isMembershipWorkspace) {
      return []
    }

    return filterSidebarEntries<SidebarNavigationEntry>(
      [...storeNavigation] as SidebarNavigationEntry[],
      effectiveCurrentRole,
    )
  }, [effectiveCurrentRole, isMembershipWorkspace, isOperationalWorkspace])

  const visibleMembershipNavigation = useMemo(() => {
    if (!SHOW_MEMBERSHIP_SIDEBAR || !isMembershipWorkspace) {
      return []
    }

    return membershipNavigation
      .filter(hasNavigationUrl)
      .filter((item) => evaluateRouteAccess(item.url.split('?')[0], effectiveCurrentRole).authorized)
  }, [effectiveCurrentRole, isMembershipWorkspace])

  const visibleFundraisingNavigation = useMemo(() => {
    if (!isFundraisingWorkspace) {
      return []
    }

    return fundraisingNavigation
      .filter(hasNavigationUrl)
      .filter((item) => evaluateRouteAccess(item.url.split('?')[0], effectiveCurrentRole).authorized)
  }, [effectiveCurrentRole, isFundraisingWorkspace])

  const venueSidebarConfigQuery = useQuery({
    queryKey: ['sidebar-venue-config', serviceInstanceId],
    queryFn: () => venueApi.getSidebarConfig(serviceInstanceId!),
    enabled: isVenueWorkspace && Boolean(serviceInstanceId),
    staleTime: 60_000,
    placeholderData: (previous) => previous,
    retry: false,
  })

  const venueWorkspaceNavigation = useMemo(
    () => normalizeVenueSidebarConfigNavigation(venueSidebarConfigQuery.data),
    [venueSidebarConfigQuery.data],
  )

  const venueWorkspaceAdminNavigation = useMemo(
    () => normalizeVenueSidebarConfigAdminNavigation(venueSidebarConfigQuery.data),
    [venueSidebarConfigQuery.data],
  )

  const visibleVenueNavigation = useMemo(() => {
    if (!isVenueWorkspace) {
      return []
    }

    return venueWorkspaceNavigation
      .filter(hasNavigationUrl)
      .filter((item) => evaluateRouteAccess(item.url.split('?')[0], effectiveCurrentRole).authorized)
  }, [effectiveCurrentRole, isVenueWorkspace, venueWorkspaceNavigation])

  const visibleVenueAdminNavigation = useMemo(() => {
    if (!isVenueWorkspace) {
      return []
    }

    return venueWorkspaceAdminNavigation
      .filter(hasNavigationUrl)
      .filter((item) => evaluateRouteAccess(item.url.split('?')[0], effectiveCurrentRole).authorized)
  }, [effectiveCurrentRole, isVenueWorkspace, venueWorkspaceAdminNavigation])

  const visibleSalesNavigation = useMemo(() => {
    if (!isOperationalWorkspace) {
      return []
    }

    return salesNavigation
      .filter(hasNavigationUrl)
      .filter((item) => evaluateRouteAccess(item.url.split('?')[0], effectiveCurrentRole).authorized)
  }, [effectiveCurrentRole, isOperationalWorkspace])

const handleLogout = async () => {
  try {
    const authStorage = localStorage.getItem('auth-storage')

    let tenantServiceInstanceId = ''

    if (authStorage) {
      const parsed = JSON.parse(authStorage)
      const state = parsed?.state ?? parsed

      // Always get TENANT post
      const tenantPost = state?.availablePosts?.find(
        (post: any) =>
          post?.level_variant_unique_id === 'head-tenant-lvl-variant' ||
          post?.service_variant_type === 'TENANT' ||
          post?.service_variant_name === 'TENANT'
      )

      tenantServiceInstanceId =
        tenantPost?.service_instance_id ?? ''
    }

    await authApi.logout()

    // clear only after getting tenant id
    localStorage.clear()

    window.location.href = tenantServiceInstanceId
      ? `/login?service_instance_id=${encodeURIComponent(
          tenantServiceInstanceId
        )}`
      : '/login'
  } catch (error) {
    console.error('Logout failed:', error)

    localStorage.clear()
    window.location.href = '/login'
  }
}

  const handleSwitchRole = () => {
    navigate(
      buildPostSelectionPath(serviceInstanceId, null),
      { replace: true },
    )
  }

  const isActive = (url?: string) => {
    if (!url) {
      return false
    }

    const [targetPath, targetSearch = ''] = url.split('?')
    const currentParams = new URLSearchParams(location.search)

    if (targetPath === '/projects' && location.pathname.startsWith('/projects')) {
      return true
    }

    if (location.pathname === targetPath) {
      if (!targetSearch) {
        return true
      }

      const targetParams = new URLSearchParams(targetSearch)
      for (const [key, value] of targetParams.entries()) {
        if (currentParams.get(key) !== value) {
          return false
        }
      }
      return true
    }
    if (
      targetPath === '/org/service-instance'
      && location.pathname.startsWith('/org/service-instance/')
      && !location.pathname.startsWith('/org/service-instance/form')
    ) {
      return true
    }
    if (targetPath === '/org/organization' && location.pathname === '/org/tree') {
      return true
    }
    if (targetPath === '/planner' && location.pathname.startsWith('/planner/')) {
      return true
    }
    if (targetPath === '/timeline' && location.pathname === '/timeline') {
      return true
    }
    if (
      (targetPath === '/services/memberships' || targetPath === '/membership/dashboard')
      && (location.pathname === '/services/memberships' || location.pathname === '/membership/dashboard')
    ) {
      if (!targetSearch) {
        return location.pathname === '/membership/dashboard'
          || new URLSearchParams(location.search).get('tab') === 'overview'
      }

      const targetParams = new URLSearchParams(targetSearch)
      const currentParams = new URLSearchParams(location.search)
      for (const [key, value] of targetParams.entries()) {
        if (currentParams.get(key) !== value) {
          return false
        }
      }
      return true
    }
    return false
  }
  const isActiveGroup = (items: ReadonlyArray<{ url: string }>) =>
    items.some((item) => location.pathname === item.url)
  const canViewBusinessDashboard = hasAnyCapability(effectiveCapabilities, [
    'can_view_business_dashboard',
    'can_access_organization_workspace',
  ])
  const canViewOrganizationTree = hasAnyCapability(effectiveCapabilities, [
    'can_view_organization_tree',
    'can_access_organization_workspace',
    'can_access_legacy_node_pages',
  ])
  const canViewBranchDirectory = hasAnyCapability(effectiveCapabilities, [
    'can_view_branch_directory',
    'can_access_branch_management',
  ])
  const canViewTenantDirectory = hasAnyCapability(effectiveCapabilities, [
    'can_view_tenant_directory',
    'can_access_tenant_directory',
  ])
  const canViewUserDirectory = hasAnyCapability(effectiveCapabilities, [
    'can_view_user_directory',
    'can_access_user_directory',
    'can_create_users',
    'can_update_users',
    'can_approve_users',
    'can_delete_users',
    'can_manage_user_lifecycle',
    'can_unlock_users',
    'can_assign_user_roles',
    'can_remove_user_roles',
  ])
  const canViewRoleDirectory = hasAnyCapability(effectiveCapabilities, [
    'can_view_role_directory',
    'can_list_role_assignments',
    'can_manage_roles',
  ])
  const canManageRoles = hasAnyCapability(effectiveCapabilities, ['can_manage_roles'])
  const canShowBranchAndInAppsItems = isOrganizationWorkspace || isTenantWorkspace || isRootOrganizationWorkspace

  const workspaceTreeItem: SidebarNavigationItem = {
    title: isBranchWorkspace ? 'My Branch' : 'My Organisation',
    url: isBranchWorkspace ? '/org/branch' : '/org/organization',
    icon: isBranchWorkspace ? GitBranch : Building2,
  }
  const shouldShowMyOrganisationItem = isTenantWorkspace || isRootOrganizationWorkspace
  const shouldForceWorkspaceTreeItem = isBranchWorkspace || shouldShowMyOrganisationItem

  const fallbackOrganizationItems = (() => {
    if (isOrganizationWorkspace) {
      return [
        ...(canViewBusinessDashboard && canShowBranchAndInAppsItems ? [organisationNavigation[0]] : []),
        ...((canViewOrganizationTree || shouldForceWorkspaceTreeItem) && shouldShowMyOrganisationItem ? [workspaceTreeItem] : []),
        ...(canViewBranchDirectory && canShowBranchAndInAppsItems && levelVariantMeta.labelOverrides.levelDirectory
          ? [{ title: levelVariantMeta.labelOverrides.levelDirectory, url: '/org/service-instance', icon: GitBranch }]
          : []),
        ...(canViewTenantDirectory && !isTenantWorkspace
          ? (levelVariantMeta.labelOverrides.parentDirectory
              ? [{ title: levelVariantMeta.labelOverrides.parentDirectory, url: '/org/tenants', icon: Landmark }]
              : [])
          : []),
        ...(canViewUserDirectory ? sidebarGroupNavItems : []),
        ...(canViewRoleDirectory ? [organisationSupportNavigation[0]] : []),
        ...(canViewRoleDirectory && canManageRoles && isRootOrganizationWorkspace ? [organisationSupportNavigation[1]] : []),
      ]
    }
    if (isBranchWorkspace) {
      return [
        ...(canViewOrganizationTree || shouldForceWorkspaceTreeItem ? [workspaceTreeItem] : []),
        ...(canViewUserDirectory ? sidebarGroupNavItems : []),
        ...(canViewRoleDirectory ? [organisationSupportNavigation[0]] : []),
        // Note: Skills are organization-scoped; not available in branch workspace
      ]
    }
    return [
      ...(canViewOrganizationTree && shouldShowMyOrganisationItem ? [workspaceTreeItem] : []),
      ...(canViewBranchDirectory && canShowBranchAndInAppsItems && levelVariantMeta.labelOverrides.levelDirectory
        ? [{ title: levelVariantMeta.labelOverrides.levelDirectory, url: '/org/service-instance', icon: GitBranch }]
        : []),
      ...(canViewTenantDirectory && levelVariantMeta.labelOverrides.parentDirectory
        ? [{ title: levelVariantMeta.labelOverrides.parentDirectory, url: '/org/tenants', icon: Landmark }]
        : []),
      ...(canViewUserDirectory ? sidebarGroupNavItems : []),
      ...(canViewRoleDirectory ? [organisationSupportNavigation[0]] : []),
      // Note: Skills are organization-scoped; not available in this scope
    ]
  })()

  const organizationItems = useMemo(() => {
    let next: SidebarNavigationItem[]
    if (organizationSidebarFeaturesQuery.data?.serviceId != null) {
      const fromFeatures = buildOrganizationSidebarItemsFromFeatures(
        organizationSidebarFeaturesQuery.data.features,
        isOrganizationWorkspace,
        isBranchWorkspace,
        canShowBranchAndInAppsItems,
        shouldShowMyOrganisationItem,
        shouldForceWorkspaceTreeItem,
        canManageRoles,
        isRootOrganizationWorkspace,
        effectiveCapabilities,
        levelVariantMeta.labelOverrides,
      )
      // Replace the user directory placeholder with dynamic group items
      next = fromFeatures.flatMap((item) =>
        item.url === '/org/users' ? sidebarGroupNavItems : [item],
      )
    } else {
      next = fallbackOrganizationItems
    }

    // Guard against accidental duplicates when capability/feature merges produce the same destination.
    const seen = new Set<string>()
    let dedupedItems = next.filter((item) => {
      if (seen.has(item.url)) {
        return false
      }
      seen.add(item.url)
      return true
    })

    // Keep a stable workspace anchor in the Organization section even while
    // capabilities/features/current role context are hydrating.
    const hasWorkspaceAnchor = dedupedItems.some(
      (item) => item.url === '/org/organization' || item.url === '/org/branch',
    )
    if (shouldForceWorkspaceTreeItem && !hasWorkspaceAnchor) {
      dedupedItems = [workspaceTreeItem, ...dedupedItems]
    }

    return dedupedItems
  }, [
    canManageRoles,
    canShowBranchAndInAppsItems,
    effectiveCapabilities,
    fallbackOrganizationItems,
    isBranchWorkspace,
    isOrganizationWorkspace,
    isRootOrganizationWorkspace,
    isTenantWorkspace,
    shouldShowMyOrganisationItem,
    workspaceTreeItem,
    shouldForceWorkspaceTreeItem,
    sidebarGroupNavItems,
    organizationSidebarFeaturesQuery.data,
  ])

  const visibleOrganizationItems = useMemo(
    () => organizationItems,
    [organizationItems],
  )
  const shouldShowTenantBranchLevels = childLevelVariants.length > 0
    && (isOrganizationWorkspace || isTenantWorkspace || isRootOrganizationWorkspace)

  const workspaceHomeCardBody = (
    <>
      {visibleActiveOrganizationLogo ? (
        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border border-border/70 bg-background shadow-sm">
          <img src={visibleActiveOrganizationLogo} alt={`${activeOrganizationName} logo`} className="h-full w-full object-contain p-1" />
        </div>
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
          <Building2 className="h-4 w-4" />
        </div>
      )}
      <div className="min-w-0 flex-1 truncate group-data-[collapsible=icon]:hidden">
        <div className="flex items-center gap-2">
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground whitespace-nowrap">
            {sidebarScopeLabel}
          </p>
          {activeOrganizationStatusLabel && activeOrganizationStatusLabel !== APPROVED_STATUS_LABEL ? (
            <Badge className={getStatusBadgeClassName(activeOrganizationStatusLabel)}>
              {activeOrganizationStatusLabel}
            </Badge>
          ) : null}
        </div>
        <p className="text-sm font-semibold">
          {shouldShowWorkspaceSelector && workspaceVariantDisplayLabel
            ? workspaceVariantDisplayLabel
            : activeOrganizationName}
        </p>
        {shouldShowWorkspaceSelector ? (
          activeOrganizationName ? (
            <p className="text-xs text-muted-foreground">{activeOrganizationName}</p>
          ) : null
        ) : workspaceVariantDisplayLabel ? (
          <p className="text-xs text-muted-foreground">{workspaceVariantDisplayLabel}</p>
        ) : null}
      </div>
      {shouldShowWorkspaceSelector ? (
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
      ) : null}
    </>
  )

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarRail />
      <SidebarHeader className="gap-4 border-b border-sidebar-border px-3 py-4 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-2">
        {shouldShowWorkspaceSelector ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Select workspace"
                className="flex w-full items-center gap-3 rounded-xl border border-sidebar-border bg-card px-3 py-3 text-left shadow-sm transition-colors hover:bg-sidebar-accent/35 group-data-[collapsible=icon]:size-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-0"
              >
                {workspaceHomeCardBody}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
              <DropdownMenuLabel>Workspace</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {workspaceServiceOptions.map((option) => (
                <DropdownMenuItem
                  key={option.key}
                  onClick={() => handleWorkspaceInstanceSelect(option)}
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{option.variantLabel}</p>
                    </div>
                    {option.instanceId === serviceInstanceId ? (
                      <Check className="h-4 w-4 shrink-0" />
                    ) : null}
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link
            to={homeCardHref}
            aria-label="Go to dashboard from active organization"
            className="flex w-full items-center gap-3 rounded-xl border border-sidebar-border bg-card px-3 py-3 text-left shadow-sm transition-colors hover:bg-sidebar-accent/35 group-data-[collapsible=icon]:size-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-0"
          >
            {workspaceHomeCardBody}
          </Link>
        )}
      </SidebarHeader>

      <SidebarContent className="px-3 py-4 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-2">
        {visibleMembershipNavigation.length > 0 ? (
          <SidebarGroup className="rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
            <SidebarGroupLabel>Membership</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleMembershipNavigation.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                      <Link to={withServiceInstanceId(item.url, serviceInstanceId)}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {isOperationalWorkspace && visibleStoreNavigation.length > 0 ? (
          <SidebarGroup className={`rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]${visibleMembershipNavigation.length > 0 ? ' mt-4' : ''}`}>
            <SidebarGroupLabel>Products &amp; Bookings</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleStoreNavigation.map((item) =>
                  item.items ? (
                    <Collapsible
                      key={item.title}
                      defaultOpen={isActiveGroup(item.items)}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton tooltip={item.title}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                            <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180 group-data-[collapsible=icon]:hidden" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {item.items.map((subItem) => (
                              <SidebarMenuSubItem key={subItem.url}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isActive(subItem.url)}
                                >
                                  <Link to={withServiceInstanceId(subItem.url, serviceInstanceId)}>{subItem.title}</Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  ) : (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <Link to={withServiceInstanceId(item.url, serviceInstanceId)}>
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {isOperationalWorkspace && visibleProjectNavigation.length > 0 ? (
          <SidebarGroup className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
            <SidebarGroupLabel>Project Management</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleProjectNavigation.map((item) =>
                  item.items ? (
                    <Collapsible
                      key={item.title}
                      defaultOpen={isActiveGroup(item.items)}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton tooltip={item.title}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                            <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180 group-data-[collapsible=icon]:hidden" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {item.items.map((subItem) => (
                              <SidebarMenuSubItem key={subItem.url}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isActive(subItem.url)}
                                >
                                  <Link to={withServiceInstanceId(subItem.url, serviceInstanceId)}>{subItem.title}</Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  ) : (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <Link to={withServiceInstanceId(item.url, serviceInstanceId)}>
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {visibleEventNavigation.length > 0 ? (
          <SidebarGroup className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
            <SidebarGroupLabel>Event Management</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleEventNavigation.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                      <Link to={withServiceInstanceId(item.url, serviceInstanceId)}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {isOperationalWorkspace && visibleMainNavigation.length > 0 ? (
          <SidebarGroup className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
            <SidebarGroupLabel>Job Management</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleMainNavigation.map((item) =>
                  item.items ? (
                    <Collapsible
                      key={item.title}
                      defaultOpen={isActiveGroup(item.items)}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton tooltip={item.title}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                            <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180 group-data-[collapsible=icon]:hidden" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {item.items.map((subItem) => (
                              <SidebarMenuSubItem key={subItem.url}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isActive(subItem.url)}
                                >
                                  <Link to={withServiceInstanceId(subItem.url, serviceInstanceId)}>{subItem.title}</Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  ) : (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <Link to={withServiceInstanceId(item.url, serviceInstanceId)}>
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {isOperationalWorkspace && visibleAssetManagementNavigation.length > 0 ? (
          <SidebarGroup className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
            <SidebarGroupLabel>Asset Management</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleAssetManagementNavigation.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                      <Link to={withServiceInstanceId(item.url, serviceInstanceId)}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {isOperationalWorkspace && visibleSalesNavigation.length > 0 ? (
          <SidebarGroup className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
            <SidebarGroupLabel>Sales</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleSalesNavigation.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                      <Link to={withServiceInstanceId(item.url, serviceInstanceId)}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {shouldShowTenantBranchLevels ? (
          <SidebarGroup
            key={`child-levels-${String(currentRole?.id ?? '')}-${currentRoleLevelVariantUniqueId}`}
            className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]"
          >
            <SidebarGroupLabel>Tenants & Branches</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {childLevelVariants.map((variant) => {
                  const lvParams = new URLSearchParams()
                  const lvUniqueId = variant.uniqueId || String(variant.id)
                  lvParams.set('lvl_variant', lvUniqueId)
                  lvParams.set('unique_level_variant_id', lvUniqueId)
                  const lvHref = withServiceInstanceId(`${variant.targetPath}?${lvParams.toString()}`, sidebarLevelVariantServiceInstanceId)
                  return (
                    <SidebarMenuItem key={variant.id}>
                      <SidebarMenuButton asChild isActive={isActive(`${variant.targetPath}?${lvParams.toString()}`)} tooltip={variant.name}>
                        <Link to={lvHref}>
                          <Layers3 className="h-4 w-4" />
                          <span>{variant.name}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {visibleFundraisingNavigation.length > 0 ? (
          <SidebarGroup className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
            <SidebarGroupLabel>Fundraising</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleFundraisingNavigation.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                      <Link to={withServiceInstanceId(item.url, fundraisingServiceInstanceId)}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {visibleVenueNavigation.length > 0 ? (
          <SidebarGroup className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
            <SidebarGroupLabel>Venue</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleVenueNavigation.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                      <Link to={withServiceInstanceId(item.url, serviceInstanceId)}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {visibleVenueAdminNavigation.length > 0 ? (
          <SidebarGroup className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
            <SidebarGroupLabel>Venue Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleVenueAdminNavigation.map((item) => (
                  <SidebarMenuItem key={`admin-${item.title}`}>
                    <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                      <Link to={withServiceInstanceId(item.url, serviceInstanceId)}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {isOperationalWorkspace && visibleFinanceNavigation.length > 0 ? (
          <SidebarGroup className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
            <SidebarGroupLabel>Finance</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {visibleFinanceNavigation.map((item) =>
                  item.items ? (
                    <Collapsible
                      key={item.title}
                      defaultOpen={isActiveGroup(item.items)}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton tooltip={item.title}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                            <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180 group-data-[collapsible=icon]:hidden" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {item.items.map((subItem) => (
                              <SidebarMenuSubItem key={subItem.url}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isActive(subItem.url)}
                                >
                                  <Link to={withServiceInstanceId(subItem.url, serviceInstanceId)}>{subItem.title}</Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  ) : (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <Link to={withServiceInstanceId(item.url, serviceInstanceId)}>
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ),
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}

        {visibleOrganizationItems.length > 0 ? (() => {
          // When Child Levels section already shows directory links, suppress duplicates here
          const childLevelPaths = shouldShowTenantBranchLevels
            ? new Set(childLevelVariants.map((v) => v.targetPath))
            : null
          const orgItemsBase = childLevelPaths
            ? visibleOrganizationItems.filter(
              (item) => item.url !== '/org/service-instance' && item.url !== '/org/tenants',
            )
            : visibleOrganizationItems
          const orgItemsToShow = childLevelPaths
            ? orgItemsBase.filter((item) => !childLevelPaths.has(item.url))
            : orgItemsBase
          if (orgItemsToShow.length === 0) return null
          return (
            <SidebarGroup className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
              <SidebarGroupLabel>{isBranchWorkspace ? 'Branch' : 'Organization'}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {orgItemsToShow.map((item) => {
                    const orgLinkHref = item.url === '/org/service-instance'
                      ? directoryLevelVariantHref
                      : item.url === '/org/branch' && myBranchSettingsHref && evaluateRouteAccess(myBranchSettingsHref, currentRole).authorized
                        ? myBranchSettingsHref
                      : item.url === '/org/tenants'
                        ? tenantDirectoryLevelVariantHref
                        : withServiceInstanceId(item.url, serviceInstanceId)
                    return (
                      <SidebarMenuItem key={`${item.title}-${item.url}`}>
                        <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                          <Link to={orgLinkHref}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )
        })() : null}

        {myBranchSettingsHref && !isRootOrganizationWorkspace && !isTenantWorkspace ? (
          <SidebarGroup className="mt-4 rounded-xl border border-sidebar-border bg-sidebar px-2 py-2 shadow-[0_8px_18px_-18px_rgba(15,23,42,0.2)]">
            <SidebarGroupLabel>Settings</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname.startsWith('/org/service-instance/form') || location.pathname.startsWith('/organization/service-instance/form')}
                    tooltip="My Branch"
                  >
                    <Link to={myBranchSettingsHref}>
                      <Settings className="h-4 w-4" />
                      <span>My Branch</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border px-3 py-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex w-full items-center gap-3 rounded-xl border border-sidebar-border bg-card px-3 py-3 text-left shadow-sm transition-colors hover:bg-sidebar-accent/65">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback>
                  {user ? getInitials(`${user.first_name} ${user.last_name}`) : 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 truncate">
                <p className="text-sm font-medium">
                  {user ? `${user.first_name} ${user.last_name}` : 'User'}
                </p>
                {currentRoleLabel ? (
                  <p className="text-xs font-medium text-muted-foreground truncate">
                    {currentRoleLabel}
                  </p>
                ) : null}
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[200px]">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {availableRoles.length > 0 ? (
              <>
                <DropdownMenuItem onClick={handleSwitchRole}>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Switch Role
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            ) : null}
            <DropdownMenuItem asChild>
              <Link to={withServiceInstanceId('/profile?tab=personal', serviceInstanceId)}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            {!isPendingUser ? (
              <DropdownMenuItem asChild>
                <Link to={withServiceInstanceId('/profile?tab=settings', serviceInstanceId)}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
